import type { LocaleResources } from 'locales';
import { KaimonoWebRoutes } from 'src/config/routes';
import type { BrandSchema } from 'src/models/brand/schema';
import type { FeatureTagSchema } from 'src/models/featureTag/schema';
import type { MakerSchema } from 'src/models/maker/schema';
import type { Product, ProductDetail } from 'src/models/product/schema';
import { LocalStorageCache } from 'src/services/cache/LocalStorageCache';
import type { Category } from 'src/stores/Category';
import { brandName } from './brandName';
import type { CloudRetailProduct } from './createDataLayerObject';
import { reformatDate } from './dateFormat';

type BreadcrumbItem = {
  unprefixedPath: string;
  label: string;
};

const breadcrumbItems = (product: ProductDetail, brands: BrandSchema[], category: Category): BreadcrumbItem[] => {
  const arr: BreadcrumbItem[] = [];
  if (product.largeCategoryId && product.largeCategorySlug) {
    const largeCategory = category.largeCategories.find((lc) => product.largeCategoryId === lc.largeCategoryId);
    arr.push({
      unprefixedPath: KaimonoWebRoutes.largeCategories.show(largeCategory?.slug ?? ''),
      label: largeCategory?.name ?? ''
    });
  }
  if (product.middleCategoryId) {
    const middleCategory = category.middleCategories.find((mc) => product.middleCategoryId === mc.middleCategoryId);
    arr.push({
      unprefixedPath: KaimonoWebRoutes.middleCategories.show(middleCategory?.slug ?? ''),
      label: middleCategory?.name ?? ''
    });
    arr.push({
      unprefixedPath: KaimonoWebRoutes.middleCategories.show(middleCategory?.slug ?? ''),
      label: middleCategory?.name ?? ''
    });
  }
  if (product.smallCategoryId) {
    const smallCategory = category.smallCategories.find((sc) => product.smallCategoryId === sc.smallCategoryId);
    arr.push({
      unprefixedPath: KaimonoWebRoutes.smallCategories.show(smallCategory?.slug ?? ''),
      label: smallCategory?.name ?? ''
    });
  }
  if (product.brandId) {
    arr.push({
      unprefixedPath: KaimonoWebRoutes.brands.show(product.brandId),
      label: brandName(product, brands)
    });
  }
  arr.push({
    unprefixedPath: KaimonoWebRoutes.products.show(product.productId),
    label: product.name
  });
  return arr;
};

const getValidBrandLogoUrl = (product: ProductDetail, brands: BrandSchema[]) => {
  const logoUrl = brands.find((brand) => brand.brandId === product.brandId)?.logoUrl;
  if (!logoUrl || logoUrl?.includes('no-image')) return null;
  return logoUrl;
};

const getBrandName = (brandId: number, brands: BrandSchema[]): string => {
  if (!brandId) return '';
  const brandName = brands.find((brand) => brand.brandId === brandId)?.name ?? '';
  return brandName;
};

const getMakerName = (makerId: number, makers: MakerSchema[]): string => {
  if (!makerId) return '';
  const makerName = makers.find((maker) => maker.makerId === makerId)?.name ?? '';
  return makerName;
};

const productPageTitle = (
  product: ProductDetail,
  localeResources: LocaleResources,
  brands: BrandSchema[],
  makers: MakerSchema[]
): string => {
  return [
    product.name,
    product.largeCategoryId === 1 && product.brandId
      ? `(${getBrandName(product.brandId, brands)})`
      : product.makerId
        ? `(${getMakerName(product.makerId, makers)})`
        : null,
    `| ${localeResources.commons.wamazingShop}`
  ]
    .filter(Boolean)
    .join(' ');
};

const mainImageUrl = (product: Product): string => {
  const images = product.images || [];
  if (images.length) {
    return images[0];
  } else {
    // FIXME better placeholder?
    return 'https://s3-ap-northeast-1.amazonaws.com/wa-kaimono-web-tokyo-development/spree/no-image.png';
  }
};

const execShowAlcoholNoticeModal = (isAlcohol: boolean, setIsAlcoholModal: (value: boolean) => void) => {
  if (isAlcohol) {
    const lastAccessDate = new LocalStorageCache('lastAccessAlcoholPageDate', {
      date: ''
    });
    const todayDate = reformatDate(new Date().toString());
    if (lastAccessDate.get() && lastAccessDate.get().date === todayDate) return;

    //初回アクセスor日付が変更されていたら初期化
    if (!lastAccessDate.get() || lastAccessDate.get().date !== todayDate) {
      lastAccessDate.set({
        date: todayDate
      });
    }
    setIsAlcoholModal(true);
  }
};

const isAlcoholProduct = (product: Product, category: Category) => {
  return (
    category.largeCategories.find((category) => category.largeCategoryId === product.largeCategoryId)?.slug ===
    'alcohol'
  );
};

const isDrugProduct = (product: Product, category: Category) => {
  return (
    category.largeCategories.find((category) => category.largeCategoryId === product.largeCategoryId)?.slug === 'drug'
  );
};

const isHomeApplianceProduct = (product: Product, category: Category) =>
  category.largeCategories.find((category) => category.largeCategoryId === product.largeCategoryId)?.slug ===
  'home_appliances';

const isLimitedSaleProduct = (product: Product, featureTags: FeatureTagSchema[]) => {
  const limitedSaleTagId = featureTags.find((tag) => tag.slug === 'limited_sale')?.featureTagId;
  if (limitedSaleTagId) {
    return product.featureTagIds.includes(limitedSaleTagId);
  }
  return false;
};

const taxProductsByPath = (pathname: string, product: Product) => {
  // TODO:商品詳細ではmedicalCanAbuseがtrueだけ表示する仕様に戻す可能性アリ
  return true;
};

const mapProductsForDataLayer = (products: Product[], quantity?: number): CloudRetailProduct[] => {
  return products.map((product) => {
    const mappedProduct = {
      product: {
        id: product.productId.toString()
      },
      quantity
    };

    return mappedProduct;
  });
};

export {
  breadcrumbItems,
  execShowAlcoholNoticeModal,
  getBrandName,
  getMakerName,
  getValidBrandLogoUrl,
  isAlcoholProduct,
  isDrugProduct,
  isHomeApplianceProduct,
  isLimitedSaleProduct,
  mainImageUrl,
  mapProductsForDataLayer,
  productPageTitle,
  taxProductsByPath
};
